import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import apiService from "../services/apiService";
import Modal from "../components/Modal";

function CourseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [activeTab, setActiveTab] = useState("lessons");
  const [modalContent, setModalContent] = useState(null);
  const [modalTitle, setModalTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const blueprintRef = useRef(null);
  const [error, setError] = useState(null);
  const [showCreateLessonModal, setShowCreateLessonModal] = useState(false);
  const [newLessonName, setNewLessonName] = useState("");
  const [creatingLesson, setCreatingLesson] = useState(false);
  
  // AQMS fields state
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedArea, setSelectedArea] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [aqmsUnits, setAqmsUnits] = useState([]);
  const [aqmsAreas, setAqmsAreas] = useState([]);
  const [aqmsTopics, setAqmsTopics] = useState([]);

  // Course outcomes
  const [outcomes, setOutcomes] = useState([]);

  // Placeholder state for future real data
  const [assignments, setAssignments] = useState([]);
  const [transcripts, setTranscripts] = useState([]);
  const [summaries, setSummaries] = useState([]);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        // Fetch course details
        const courseData = await apiService.getCourseById(id);
        setCourse(courseData);

        // Fetch lessons for this course
        const lessonsData = await apiService.getLessonsByCourse(id);
        setLessons(lessonsData);

        // Fetch outcomes
        try {
          const outcomesData = await apiService.getOutcomesByCourse(id);
          setOutcomes(outcomesData || []);
        } catch (e) { setOutcomes([]); }
      } catch (err) {
        setError("Failed to load course details.");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [id]);

  // Extract AQMS data when course loads
  useEffect(() => {
    if (course && course.imported_from_aqms && course.aqms_course_data) {
      const aqmsData = course.aqms_course_data;
      
      // Extract Units, Areas, Topics from AQMS data
      setAqmsUnits(aqmsData.courseUnits || []);
      setAqmsAreas(aqmsData.courseAreas || []);
      setAqmsTopics(aqmsData.courseTopics || []);
    } else {
      // Clear AQMS data if not an AQMS course
      setAqmsUnits([]);
      setAqmsAreas([]);
      setAqmsTopics([]);
    }
  }, [course]);

  const openModal = (content, title) => {
    setModalContent(content);
    setModalTitle(title);
  };

  const closeModal = () => {
    setModalContent(null);
    setModalTitle("");
  };

  const handleUploadBlueprint = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const data = await apiService.uploadBlueprint(id, file);
      alert(`Blueprint uploaded: ${data.rows} entries, ${data.cos_created} new COs created`);
      // Refresh outcomes
      try {
        const outcomesData = await apiService.getOutcomesByCourse(id);
        setOutcomes(outcomesData || []);
      } catch (err) {}
    } catch (err) {
      alert('Upload failed: ' + err.message);
    }
    e.target.value = '';
  };

  const handleDownloadBlueprint = async () => {
    try {
      const res = await apiService.downloadBlueprint(id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `blueprint_${course?.title || 'course'}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('No blueprint uploaded for this course');
    }
  };

  const openCreateLessonModal = () => {
    setNewLessonName("");
    setShowCreateLessonModal(true);
  };

  const closeCreateLessonModal = () => {
    setShowCreateLessonModal(false);
    setNewLessonName("");
    // Reset AQMS selections
    setSelectedUnit(null);
    setSelectedArea(null);
    setSelectedTopic(null);
  };

  const handleCreateLesson = async () => {
    if (!newLessonName.trim()) {
      alert("Please enter a lesson name.");
      return;
    }

    setCreatingLesson(true);
    try {
      const lessonData = {
        title: newLessonName.trim(),
        file: "",
        type: "lesson",
        audio_transcript: "",
        summary: "",
        course: parseInt(id, 10),
      };

      // Add AQMS fields if selected
      if (selectedUnit) {
        lessonData.aqms_unit_id = selectedUnit.id;
        lessonData.aqms_unit_name = selectedUnit.name || "";
      }
      if (selectedArea) {
        lessonData.aqms_area_id = selectedArea.id;
        lessonData.aqms_area_name = selectedArea.name || "";
      }
      if (selectedTopic) {
        lessonData.aqms_topic_id = selectedTopic.id;
        lessonData.aqms_topic_name = selectedTopic.name || "";
      }

      const newLesson = await apiService.createLesson(lessonData);

      // Refresh lessons list
      const lessonsData = await apiService.getLessonsByCourse(id);
      setLessons(lessonsData);

      closeCreateLessonModal();
      alert(`Lesson "${newLessonName}" created successfully!`);
    } catch (err) {
      console.error("Error creating lesson:", err);
      alert(err.message || "Failed to create lesson. Please try again.");
    } finally {
      setCreatingLesson(false);
    }
  };

  // Helper to determine content type from file URL
  const getContentTypeFromUrl = (fileUrl) => {
    if (!fileUrl) return "unknown";

    const url = fileUrl.toLowerCase();

    // Check for video files
    if (
      url.includes(".mp4") ||
      url.includes("video") ||
      url.includes("youtube.com") ||
      url.includes("youtu.be")
    ) {
      return "video";
    }

    // Check for PDF files
    if (url.includes(".pdf") || url.includes("pdf")) {
      return "pdf";
    }

    // Check for text files
    if (url.includes(".txt") || url.includes("text")) {
      return "text";
    }

    // Default fallback - if we can't determine, assume it's a document
    return "document";
  };

  // Helper to get Google Docs viewer URL
  const getGoogleDocsViewerUrl = (pdfUrl) => {
    return `https://docs.google.com/viewer?url=${encodeURIComponent(
      pdfUrl
    )}&embedded=true`;
  };

  // Helper to render video, YouTube embed, or PDF based on file URL
  const getVideoContent = (lesson) => {
    const contentType = getContentTypeFromUrl(lesson.file);

    // Handle PDF and text files (documents)
    if (
      contentType === "pdf" ||
      contentType === "text" ||
      contentType === "document"
    ) {
      const googleDocsViewerUrl = getGoogleDocsViewerUrl(lesson.file);

      return (
        <div style={{ padding: "10px" }}>
          {/* Download button */}
          <div style={{ marginBottom: "15px", textAlign: "right" }}>
            <a
              href={lesson.file}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: "8px 16px",
                backgroundColor: "#dc3545",
                color: "white",
                textDecoration: "none",
                borderRadius: "4px",
                fontSize: "14px",
                display: "inline-block",
              }}
            >
              📥 Download PDF
            </a>
          </div>

          {/* PDF Iframe with Google Docs Viewer */}
          <div
            style={{
              border: "1px solid #ddd",
              borderRadius: "4px",
              overflow: "hidden",
            }}
          >
            <iframe
              src={googleDocsViewerUrl}
              title={lesson.title}
              width="100%"
              height="750px"
              style={{ border: "none" }}
            />
          </div>
        </div>
      );
    }

    // Handle video files (including YouTube and MP4)
    if (contentType === "video") {
      // Handle YouTube videos
      if (
        lesson.file &&
        (lesson.file.includes("youtube.com") ||
          lesson.file.includes("youtu.be"))
      ) {
        // Extract YouTube video ID
        let videoId = "";
        if (lesson.file.includes("youtube.com")) {
          const urlParams = new URLSearchParams(lesson.file.split("?")[1]);
          videoId = urlParams.get("v");
        } else if (lesson.file.includes("youtu.be")) {
          videoId = lesson.file.split("/").pop().split("?")[0];
        }
        if (videoId) {
          const embedUrl = `https://www.youtube.com/embed/${videoId}`;
          return (
            <iframe
              src={embedUrl}
              title={lesson.title}
              width="100%"
              height="400"
              style={{ border: "none" }}
              allowFullScreen
            />
          );
        }
      }

      // Handle MP4 and other video files
      return (
        <video
          src={lesson.file}
          controls
          width="100%"
          style={{ maxHeight: "60vh" }}
        />
      );
    }

    // Fallback for unknown content types
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <p>Unable to display this content type.</p>
        <a
          href={lesson.file}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            padding: "10px 20px",
            backgroundColor: "#007bff",
            color: "white",
            textDecoration: "none",
            borderRadius: "4px",
          }}
        >
          Open in New Tab
        </a>
      </div>
    );
  };

  if (loading) return <div>Loading course details...</div>;
  if (error) return <div>{error}</div>;
  if (!course) return <div>No course found.</div>;

  const renderLessonTab = () => {
    if (!lessons.length) return <p>No lessons uploaded for this course.</p>;
    return (
      <div>
        {lessons.map((lesson) => {
          const contentType = getContentTypeFromUrl(lesson.file);
          const icon = contentType === "video" ? "📹" : "📄";

          return (
            <div key={lesson.id} className="lesson-card">
              <h4>
                {icon} {lesson.title}
              </h4>
              <div className="quick-actions">
                <button
                  className="btn btn-primary"
                  onClick={() => navigate(`/lessons/${lesson.id}/resources`)}
                >
                  View Lesson Content
                </button>
                <button
                  className="btn"
                  style={{ background: '#F56A3F', color: '#fff', marginLeft: '8px' }}
                  onClick={() => navigate(`/lecture-player/${lesson.id}`)}
                >
                  Lecture Player
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Placeholder render functions for other tabs
  const renderAssignmentsTab = () => (
    <div>
      <p>Assignments tab coming soon.</p>
      {/* Map over assignments when real data is available */}
    </div>
  );
  const renderTranscriptsTab = () => (
    <div>
      <p>Transcripts tab coming soon.</p>
      {/* Map over transcripts when real data is available */}
    </div>
  );
  const renderSummariesTab = () => (
    <div>
      <p>Summaries tab coming soon.</p>
      {/* Map over summaries when real data is available */}
    </div>
  );

  return (
    <div>
      <div className="course-detail-header">
        <button
          className="btn btn-secondary"
          onClick={() => navigate("/courses")}
        >
          ← Back to Courses
        </button>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginTop: "1rem",
          }}
        >
          <div style={{ flex: 1 }}>
            <h1 id="course-detail-title">{course.title}</h1>
            <p
              id="course-detail-subtitle"
              style={{ color: "var(--text-light)" }}
            >
              {course.department}
            </p>
          </div>
          <button
            className="btn btn-primary add-course-btn"
            onClick={openCreateLessonModal}
            style={{ marginLeft: "1rem" }}
          >
            <span>+</span> Create Lesson
          </button>
          <button
            className="btn"
            style={{ background: '#28a745', color: '#fff', marginLeft: '0.5rem' }}
            onClick={() => blueprintRef.current?.click()}
          >
            📤 Upload Blueprint
          </button>
          <button
            className="btn"
            style={{ background: '#4a90e2', color: '#fff', marginLeft: '0.5rem' }}
            onClick={handleDownloadBlueprint}
          >
            📥 Download Blueprint
          </button>
          <button
            className="btn btn-outline"
            style={{ marginLeft: '0.5rem', border: '1px solid #ccc', color: '#555' }}
            title="Download a CSV with the exact column headers our blueprint parser expects, plus example rows."
            onClick={async () => {
              try {
                await apiService.downloadQuestionSetTemplate('blueprint');
              } catch (err) {
                alert('Could not download template: ' + err.message);
              }
            }}
          >
            📥 Download Template
          </button>
          <input
            ref={blueprintRef}
            type="file"
            accept=".csv"
            onChange={handleUploadBlueprint}
            style={{ display: 'none' }}
          />
        </div>
      </div>
      <div className="course-detail-tabs">
        <div
          className={`course-detail-tab ${
            activeTab === "lessons" ? "active" : ""
          }`}
          onClick={() => setActiveTab("lessons")}
        >
          Lessons ({lessons.length})
        </div>
        <div
          className={`course-detail-tab ${activeTab === "outcomes" ? "active" : ""}`}
          onClick={() => setActiveTab("outcomes")}
        >
          Course Outcomes ({outcomes.length})
        </div>
        {/* TODO: All Assignments - commented out until content is ready
        <div className={`course-detail-tab ${activeTab === "assignments" ? "active" : ""}`} onClick={() => setActiveTab("assignments")}>All Assignments</div>
        */}
        {/* TODO: All Transcripts - commented out until content is ready
        <div className={`course-detail-tab ${activeTab === "transcripts" ? "active" : ""}`} onClick={() => setActiveTab("transcripts")}>All Transcripts</div>
        */}
        {/* TODO: All Summaries - commented out until content is ready
        <div className={`course-detail-tab ${activeTab === "summaries" ? "active" : ""}`} onClick={() => setActiveTab("summaries")}>All Summaries</div>
        */}
      </div>
      <div id="course-detail-tab-content">
        {activeTab === "lessons" && renderLessonTab()}
        {activeTab === "outcomes" && (
          <div>
            {outcomes.length === 0 ? (
              <p>No course outcomes defined yet.</p>
            ) : (
              <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ background: '#042F54', color: '#fff' }}>
                      <th style={{ padding: '12px 16px', textAlign: 'left', width: '100px' }}>Code</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left' }}>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {outcomes.map((o, i) => (
                      <tr key={o.id} style={{ background: i % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                        <td style={{ padding: '10px 16px', fontWeight: 600, color: '#042F54' }}>{o.short_name || `CO${i+1}`}</td>
                        <td style={{ padding: '10px 16px', color: '#333' }}>{o.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        {/* TODO: Assignments tab content - commented out until content is ready
        {activeTab === "assignments" && renderAssignmentsTab()}
        */}
        {/* TODO: Transcripts tab content - commented out until content is ready
        {activeTab === "transcripts" && renderTranscriptsTab()}
        */}
        {/* TODO: Summaries tab content - commented out until content is ready
        {activeTab === "summaries" && renderSummariesTab()}
        */}
      </div>
      <Modal isOpen={!!modalContent} onClose={closeModal} title={modalTitle}>
        {modalContent}
      </Modal>

      {/* Create Lesson Modal */}
      {showCreateLessonModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2 className="modal-title">Create New Lesson</h2>
                <span className="close-btn" onClick={closeCreateLessonModal}>
                  &times;
                </span>
              </div>
              {course?.title && (
                <p style={{ color: "var(--text-light)", marginTop: "0.5rem", marginBottom: "0", width: '100%' }}>
                  Course: <strong>{course.title}</strong>
                </p>
              )}
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">New Lesson Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={newLessonName}
                  onChange={(e) => setNewLessonName(e.target.value)}
                  placeholder="Enter a name for your new lesson"
                  disabled={creatingLesson}
                />
              </div>

              {/* AQMS Fields - Only show for AQMS courses */}
              {course?.imported_from_aqms && course?.aqms_course_data && (
                <>
                  <hr style={{ margin: "1.5rem 0" }} />
                  <h4 style={{ marginBottom: "1rem" }}>AQMS Course Structure</h4>
                  
                  {/* Unit, Area, Topic in single row */}
                  <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                    {/* Unit Select */}
                    <div className="form-group" style={{ flex: 1, minWidth: "200px" }}>
                      <label className="form-label">Unit</label>
                      <select
                        className="form-control"
                        value={selectedUnit?.id || ""}
                        onChange={(e) => {
                          const unitId = parseInt(e.target.value);
                          const unit = aqmsUnits.find(u => u.id === unitId);
                          setSelectedUnit(unit || null);
                        }}
                        disabled={creatingLesson}
                      >
                        <option value="">Select Unit</option>
                        {aqmsUnits.length > 0 ? (
                          aqmsUnits.map((unit) => (
                            <option key={unit.id} value={unit.id}>
                              {unit.name || `Unit ${unit.id}`}
                            </option>
                          ))
                        ) : (
                          <option value="" disabled>No units available</option>
                        )}
                      </select>
                    </div>

                    {/* Area Select */}
                    <div className="form-group" style={{ flex: 1, minWidth: "200px" }}>
                      <label className="form-label">Area</label>
                      <select
                        className="form-control"
                        value={selectedArea?.id || ""}
                        onChange={(e) => {
                          const areaId = parseInt(e.target.value);
                          const area = aqmsAreas.find(a => a.id === areaId);
                          setSelectedArea(area || null);
                        }}
                        disabled={creatingLesson}
                      >
                        <option value="">Select Area</option>
                        {aqmsAreas.length > 0 ? (
                          aqmsAreas.map((area) => (
                            <option key={area.id} value={area.id}>
                              {area.name || `Area ${area.id}`}
                            </option>
                          ))
                        ) : (
                          <option value="" disabled>No areas available</option>
                        )}
                      </select>
                    </div>

                    {/* Topic Select */}
                    <div className="form-group" style={{ flex: 1, minWidth: "200px" }}>
                      <label className="form-label">Topic</label>
                      <select
                        className="form-control"
                        value={selectedTopic?.id || ""}
                        onChange={(e) => {
                          const topicId = parseInt(e.target.value);
                          const topic = aqmsTopics.find(t => t.id === topicId);
                          setSelectedTopic(topic || null);
                        }}
                        disabled={creatingLesson}
                      >
                        <option value="">Select Topic</option>
                        {aqmsTopics.length > 0 ? (
                          aqmsTopics.map((topic) => (
                            <option key={topic.id} value={topic.id}>
                              {topic.name || `Topic ${topic.id}`}
                            </option>
                          ))
                        ) : (
                          <option value="" disabled>No topics available</option>
                        )}
                      </select>
                    </div>
                  </div>
                </>
              )}

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "1rem",
                  marginTop: "1.5rem",
                }}
              >
                <button
                  className="btn btn-secondary"
                  onClick={closeCreateLessonModal}
                  disabled={creatingLesson}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleCreateLesson}
                  disabled={creatingLesson || !newLessonName.trim()}
                >
                  {creatingLesson ? "Creating..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CourseDetail;
